import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { attachedFront, bodyRadiusAt, layoutStack, stackRadius, type Layout, type Rocket } from '@openrocket-web/physics';
import type { Fmt } from './format';

interface Props {
  readonly rocket: Rocket;
  readonly cgX: number | undefined;
  readonly cpX: number | undefined;
  readonly selectedId: string | undefined;
  readonly fmt: Fmt;
  /** Dibuja los símbolos de CG y CP. */
  readonly showMarkers: boolean;
  /** `'fit'` ajusta a la ventana; un número es la escala absoluta (1 = 100 %, a 96 píxeles por pulgada). */
  readonly scale: 'fit' | number;
  /** Vista lateral o vista desde atrás (la cola del cohete de frente). */
  readonly view: 'side' | 'back';
  /** Giro del cohete sobre su eje, en grados: cambia cómo se proyectan las aletas. */
  readonly roll: number;
  /** Avisa del porcentaje al que queda el ajuste a la ventana. */
  readonly onFit?: (percent: number) => void;
  readonly onSelect: (id: string) => void;
  readonly onEdit: () => void;
}

const MM = 1000;
/** Píxeles por milímetro al 100 % (96 píxeles por pulgada). */
export const PX_PER_MM = 96 / 25.4;
const num = (v: number): string => (Math.round(v * 100) / 100).toString();
const pts = (p: ReadonlyArray<readonly [number, number]>): string => p.map(([x, y]) => `${num(x)},${num(y)}`).join(' ');

// Márgenes en píxeles: las reglas y los textos superpuestos de DesignView ocupan estas franjas.
const PAD_L = 80;
const PAD_R = 28;
const PAD_T = 76;
const PAD_B = 52;
const RULER = 20;
const CORNER = 40;
const FALLBACK = { w: 1200, h: 420 };

function profile(entry: Layout, samples = 48): Array<[number, number]> {
  const c = entry.component;
  const out: Array<[number, number]> = [];
  const n = c.type === 'nose-cone' ? samples : 2;
  for (let i = 0; i <= n; i++) {
    const x = (i / n) * c.length;
    out.push([(entry.x0 + x) * MM, stackRadius(c, x) * MM]);
  }
  return out;
}

/** Contorno cerrado simétrico: borde superior de punta a cola y borde inferior de vuelta. */
function outline(entry: Layout): string {
  const top = profile(entry);
  const bottom = [...top].reverse().map(([x, r]): [number, number] => [x, r]);
  return pts([...top.map(([x, r]): [number, number] => [x, -r]), ...bottom.map(([x, r]): [number, number] => [x, r])]);
}

/**
 * Tamaño disponible del dibujo. Se mide el marco, que no tiene barras de desplazamiento, y no el elemento
 * que se desplaza: si se midiera este, aparecer una barra encogería el área, el dibujo se reajustaría, la barra
 * desaparecería y el ciclo no pararía (el parpadeo). Se redondea hacia abajo porque el diseño es fraccionario
 * y un píxel de más basta para desbordar. En jsdom (sin diseño) se usa un valor fijo.
 */
function useSize(ref: React.RefObject<HTMLElement>): { w: number; h: number } {
  const [size, setSize] = useState(FALLBACK);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return;
    const read = (): void => {
      const r = el.getBoundingClientRect();
      const w = Math.floor(r.width);
      const h = Math.floor(r.height);
      if (w > 0 && h > 0) setSize((p) => (p.w === w && p.h === h ? p : { w, h }));
    };
    read();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

let scrollbarCache: number | undefined;
/** Grosor de una barra de desplazamiento clásica (0 con barras superpuestas y en jsdom). */
function scrollbarSize(): number {
  if (scrollbarCache !== undefined) return scrollbarCache;
  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll;visibility:hidden';
  document.body.append(probe);
  scrollbarCache = probe.offsetWidth - probe.clientWidth;
  probe.remove();
  return scrollbarCache;
}

/**
 * Tamaño del lienzo y si hace falta desplazarse. Solo hay barras cuando el contenido de verdad no cabe, y una
 * barra que aparece le quita sitio a la otra dirección: se cuenta aquí en lugar de dejar que el navegador la
 * abra sin necesidad. El área medida nunca depende de las barras, así que no hay bucle.
 */
export function canvasSize(area: { w: number; h: number }, content: { w: number; h: number }, bar: number, fitToWindow = false): { svgW: number; svgH: number; scroll: boolean } {
  // «Ajustar a la ventana» nunca se desplaza: si la mitad queda muy baja, se recorta en lugar de abrir y cerrar barras
  // al arrastrar el separador o redimensionar justo en el límite.
  if (fitToWindow) return { svgW: area.w, svgH: area.h, scroll: false };
  const EPS = 0.5;
  let needV = content.h > area.h + EPS;
  let needH = content.w > area.w + EPS;
  if (needV && !needH) needH = content.w > area.w - bar + EPS;
  if (needH && !needV) needV = content.h > area.h - bar + EPS;
  return {
    svgW: Math.max(area.w - (needV ? bar : 0), content.w),
    svgH: Math.max(area.h - (needH ? bar : 0), content.h),
    scroll: needV || needH,
  };
}

const STEPS = [0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500];

/** Marcas de una regla: la primera separación que deja al menos 44 px entre números. */
function ticks(from: number, to: number, pxPerUnit: number): { major: number[]; minor: number[]; step: number } {
  const step = STEPS.find((s) => s * pxPerUnit >= 44) ?? STEPS[STEPS.length - 1]!;
  const minorStep = step / 5;
  const major: number[] = [];
  const minor: number[] = [];
  for (let v = Math.ceil(from / minorStep) * minorStep; v <= to; v += minorStep) {
    const r = Math.round(v / minorStep) * minorStep;
    if (Math.abs(r / step - Math.round(r / step)) < 1e-6) major.push(Math.round(r * 100) / 100);
    else minor.push(r);
  }
  return { major, minor, step };
}

export function RocketView({ rocket, cgX, cpX, selectedId, fmt, showMarkers, scale, view, roll, onFit, onSelect, onEdit }: Props): JSX.Element {
  const back = view === 'back';
  const frame = useRef<HTMLDivElement>(null);
  const { w: W, h: H } = useSize(frame);

  const layout = layoutStack(rocket);
  const length = layout.length === 0 ? 0 : layout[layout.length - 1]!.x1 * MM;
  let extent = 0;
  const shapes: JSX.Element[] = [];
  const attachedShapes: JSX.Element[] = [];
  const backFins: Array<{ id: string; count: number; rb: number; span: number; thickness: number; selected: boolean }> = [];
  const backRings: Array<{ id: string; type: string; r: number; selected: boolean }> = [];
  const backCrowns: Array<{ id: string; type: string; ro: number; ri: number; selected: boolean }> = [];
  const pick = (id: string) => ({ 'data-component': id, onClick: () => onSelect(id), onDoubleClick: onEdit });

  for (const entry of layout) {
    const c = entry.component;
    const maxR = c.type === 'nose-cone' ? c.baseDiameter / 2 : c.type === 'body-tube' ? c.outerDiameter / 2 : Math.max(c.foreDiameter, c.aftDiameter) / 2;
    extent = Math.max(extent, maxR * MM);
    backRings.push({ id: c.id, type: c.type, r: maxR * MM, selected: selectedId === c.id });
    shapes.push(<polygon key={c.id} className={`part part--${c.type}${selectedId === c.id ? ' is-selected' : ''}`} points={outline(entry)} {...pick(c.id)} />);
    if (c.type === 'nose-cone' && c.shoulder !== undefined) {
      // El hombro entra en el tubo, por detrás de la ojiva; al pulsarlo se selecciona la ojiva.
      const r = (c.shoulder.diameter / 2) * MM;
      attachedShapes.push(<rect key={`${c.id}-shoulder`} className={`part part--shoulder${selectedId === c.id ? ' is-selected' : ''}`} x={num(entry.x1 * MM)} y={num(-r)} width={num(c.shoulder.length * MM)} height={num(2 * r)} {...pick(c.id)} />);
    }
    if (c.type === 'nose-cone') continue;
    for (const k of c.children) {
      const sel = selectedId === k.id ? ' is-selected' : '';
      if (k.type === 'fin-set') {
        const front = attachedFront(entry, k.rootChord, k.position);
        const rb = bodyRadiusAt(entry, front + k.rootChord / 2);
        // Cada aleta sale en una dirección radial; al verla de lado solo cuenta su proyección vertical.
        const polys: JSX.Element[] = [];
        for (let i = 0; i < k.finCount; i++) {
          const c = Math.cos(((roll + (i * 360) / k.finCount) * Math.PI) / 180);
          if (Math.abs(c) < 0.02) continue; // vista de canto: no se ve su superficie
          polys.push(
            <polygon
              key={i}
              points={pts([
                [front * MM, -c * rb * MM],
                [(front + k.sweep) * MM, -c * (rb + k.span) * MM],
                [(front + k.sweep + k.tipChord) * MM, -c * (rb + k.span) * MM],
                [(front + k.rootChord) * MM, -c * rb * MM],
              ])}
            />,
          );
        }
        extent = Math.max(extent, (rb + k.span) * MM);
        attachedShapes.push(
          <g key={k.id} className={`part part--fin${sel}`} {...pick(k.id)}>
            {polys}
          </g>,
        );
        backFins.push({ id: k.id, count: k.finCount, rb: rb * MM, span: k.span * MM, thickness: Math.max(k.thickness * MM, 1.5), selected: selectedId === k.id });
      } else if (k.type === 'inner-tube') {
        const front = attachedFront(entry, k.length, k.position);
        const r = (k.innerDiameter / 2 + k.wallThickness) * MM;
        attachedShapes.push(<rect key={k.id} className={`part part--mount${sel}`} x={num(front * MM)} y={num(-r)} width={num(k.length * MM)} height={num(2 * r)} {...pick(k.id)} />);
      } else if (k.type === 'centering-ring' || k.type === 'bulkhead' || k.type === 'engine-block') {
        // Coronas dentro del tubo: dos paredes finas si tienen hueco y un solo rectángulo si son macizas.
        const front = attachedFront(entry, k.length, k.position);
        const ro = (k.outerDiameter / 2) * MM;
        const ri = Math.min((k.innerDiameter / 2) * MM, ro);
        const cls = `part part--crown${sel}`;
        const rect = (key: string, y: number, h: number): JSX.Element => <rect key={key} className={cls} x={num(front * MM)} y={num(y)} width={num(k.length * MM)} height={num(h)} {...pick(k.id)} />;
        attachedShapes.push(ri > 0 ? <g key={k.id}>{[rect('top', -ro, ro - ri), rect('bottom', ri, ro - ri)]}</g> : rect(k.id, -ro, 2 * ro));
        backCrowns.push({ id: k.id, type: k.type, ro, ri, selected: selectedId === k.id });
      } else if (k.type === 'shock-cord') {
        const front = attachedFront(entry, k.length, k.position);
        const r = Math.max(stackRadius(c, front + k.length / 2 - entry.x0) - (c.wallThickness + 0.001), 0.002) * MM;
        const zig = Array.from({ length: 9 }, (_, i): [number, number] => [(front + (k.length * i) / 8) * MM, (i % 2 === 0 ? -0.4 : 0.4) * r]);
        attachedShapes.push(
          <g key={k.id} className={`part part--cord${sel}`} {...pick(k.id)}>
            <rect x={num(front * MM)} y={num(-r)} width={num(k.length * MM)} height={num(2 * r)} />
            <polyline points={pts(zig)} />
          </g>,
        );
      } else if (k.type === 'mass-component') {
        const front = attachedFront(entry, k.length, k.position);
        const r = Math.max(stackRadius(c, front + k.length / 2 - entry.x0) - (c.wallThickness + 0.001), 0.002) * MM;
        attachedShapes.push(<rect key={k.id} className={`part part--mass${sel}`} x={num(front * MM)} y={num(-r * 0.8)} width={num(k.length * MM)} height={num(1.6 * r)} {...pick(k.id)} />);
      } else if (k.type === 'launch-lug') {
        // El soporte para guía va por fuera del tubo, en la parte superior del dibujo.
        const front = attachedFront(entry, k.length, k.position);
        const rb = bodyRadiusAt(entry, front + k.length / 2);
        extent = Math.max(extent, (rb + k.outerDiameter) * MM);
        attachedShapes.push(<rect key={k.id} className={`part part--lug${sel}`} x={num(front * MM)} y={num(-(rb + k.outerDiameter) * MM)} width={num(k.length * MM)} height={num(k.outerDiameter * MM)} {...pick(k.id)} />);
      } else {
        const front = attachedFront(entry, k.packedLength, k.position);
        const r = Math.max(stackRadius(c, front + k.packedLength / 2 - entry.x0) - (c.wallThickness + 0.001), 0.002) * MM;
        attachedShapes.push(<rect key={k.id} className={`part part--chute${sel}`} x={num(front * MM)} y={num(-r * 0.8)} width={num(k.packedLength * MM)} height={num(1.6 * r)} rx="2" {...pick(k.id)} />);
      }
    }
  }

  // Escala en píxeles por milímetro: ajustada a la ventana y multiplicada por el zoom.
  // Un píxel de holgura: con «Ajustar» el dibujo debe caber siempre, sin barras.
  const plotW = Math.max(W - PAD_L - PAD_R - 1, 40);
  const plotH = Math.max(H - PAD_T - PAD_B - 1, 20);
  const fit = back ? Math.min(plotW, plotH) / Math.max(2 * extent, 1) : length === 0 ? 1 : Math.min(plotW / length, plotH / Math.max(2 * extent, 1));
  const s = scale === 'fit' ? fit : scale * PX_PER_MM;
  const fitPercent = Math.round((fit / PX_PER_MM) * 100);
  useEffect(() => onFit?.(fitPercent), [fitPercent, onFit]);
  const contentW = PAD_L + (back ? 2 * extent : length) * s + PAD_R;
  const contentH = PAD_T + 2 * extent * s + PAD_B;
  const { svgW, svgH, scroll } = canvasSize({ w: W, h: H }, { w: contentW, h: contentH }, scrollbarSize(), scale === 'fit');
  const ox = PAD_L + (svgW - contentW) / 2 + (back ? extent * s : 0);
  const cy = PAD_T + (svgH - PAD_T - PAD_B) / 2;

  const unitMm = fmt.system === 'metric' ? 10 : 25.4;
  const unitLabel = fmt.system === 'metric' ? 'cm' : 'in';
  const pxPerUnit = unitMm * s;
  const hx = ticks((0 - ox) / pxPerUnit, (svgW - ox) / pxPerUnit, pxPerUnit);
  const vy = ticks((cy - svgH) / pxPerUnit, cy / pxPerUnit, pxPerUnit);

  const markerX = (x: number | undefined): number | undefined => (x === undefined || !Number.isFinite(x) ? undefined : ox + x * MM * s);
  const cgPx = showMarkers ? markerX(cgX) : undefined;
  const cpPx = showMarkers ? markerX(cpX) : undefined;

  const description = back
    ? layout.length === 0
      ? 'Vista trasera del cohete: sin componentes.'
      : `Vista trasera del cohete, diámetro máximo ${fmt.dimension((2 * extent) / MM)}.`
    : layout.length === 0
      ? 'Vista lateral del cohete: sin componentes.'
      : `Vista lateral del cohete, longitud ${fmt.dimension(length / MM)}${cgX === undefined ? '' : `, centro de gravedad a ${fmt.dimension(cgX)} de la punta`}${cpX === undefined || !Number.isFinite(cpX) ? '' : `, centro de presión a ${fmt.dimension(cpX)} de la punta`}.`;

  return (
    <div className="rocket-view__frame" ref={frame}>
      <div className="rocket-view__scroll" style={{ overflow: scroll ? 'auto' : 'hidden' }}>
        <figure className="rocket-view">
          <svg width={num(svgW)} height={num(svgH)} viewBox={`0 0 ${num(svgW)} ${num(svgH)}`} role="img" aria-label={description} data-length-mm={num(length)}>
            <title>{description}</title>

            <g className="ruler" aria-hidden="true">
              <line x1="0" x2={num(svgW)} y1={RULER} y2={RULER} />
              {hx.minor.filter((v) => ox + v * pxPerUnit > CORNER).map((v) => (
                <line key={`m${v}`} x1={num(ox + v * pxPerUnit)} x2={num(ox + v * pxPerUnit)} y1={RULER - 4} y2={RULER} />
              ))}
              {hx.major.filter((v) => ox + v * pxPerUnit > CORNER).map((v) => (
                <g key={`M${v}`}>
                  <line x1={num(ox + v * pxPerUnit)} x2={num(ox + v * pxPerUnit)} y1={RULER - 9} y2={RULER} />
                  <text x={num(ox + v * pxPerUnit + 2)} y={RULER - 9}>
                    {v}
                  </text>
                </g>
              ))}
              <text x="4" y={RULER - 6} className="ruler__unit">
                {unitLabel}
              </text>
              <line x1={PAD_L - 12} x2={PAD_L - 12} y1={RULER} y2={num(svgH)} />
              {vy.minor.filter((v) => cy - v * pxPerUnit > RULER + 10).map((v) => (
                <line key={`m${v}`} x1={PAD_L - 16} x2={PAD_L - 12} y1={num(cy - v * pxPerUnit)} y2={num(cy - v * pxPerUnit)} />
              ))}
              {vy.major.filter((v) => cy - v * pxPerUnit > RULER + 10).map((v) => (
                <g key={`M${v}`}>
                  <line x1={PAD_L - 21} x2={PAD_L - 12} y1={num(cy - v * pxPerUnit)} y2={num(cy - v * pxPerUnit)} />
                  <text x={PAD_L - 23} y={num(cy - v * pxPerUnit + 4)} textAnchor="end">
                    {v}
                  </text>
                </g>
              ))}
            </g>

            <g transform={`translate(${num(ox)} ${num(cy)}) scale(${s.toFixed(5)})`}>
              {back ? (
                <>
                  {[...backRings]
                    .sort((p, q) => q.r - p.r)
                    .map((r) => (
                      <circle key={r.id} r={r.r} className={`part part--${r.type}${r.selected ? ' is-selected' : ''}`} {...pick(r.id)} />
                    ))}
                  {backCrowns.map((r) => (
                    <path
                      key={r.id}
                      className={`part part--crown${r.selected ? ' is-selected' : ''}`}
                      fillRule="evenodd"
                      d={`M${num(r.ro)} 0 A${num(r.ro)} ${num(r.ro)} 0 1 0 ${num(-r.ro)} 0 A${num(r.ro)} ${num(r.ro)} 0 1 0 ${num(r.ro)} 0Z${r.ri > 0 ? ` M${num(r.ri)} 0 A${num(r.ri)} ${num(r.ri)} 0 1 0 ${num(-r.ri)} 0 A${num(r.ri)} ${num(r.ri)} 0 1 0 ${num(r.ri)} 0Z` : ''}`}
                      {...pick(r.id)}
                    />
                  ))}
                  {backFins.flatMap((f) =>
                    Array.from({ length: f.count }, (_, i) => (
                      <rect
                        key={`${f.id}-${i}`}
                        x={-f.thickness / 2}
                        y={-(f.rb + f.span)}
                        width={f.thickness}
                        height={f.span}
                        transform={`rotate(${roll + (i * 360) / f.count})`}
                        className={`part part--fin${f.selected ? ' is-selected' : ''}`}
                        {...pick(f.id)}
                      />
                    )),
                  )}
                </>
              ) : (
                <>
                  {shapes}
                  {attachedShapes}
                </>
              )}
            </g>

            {!back && cgPx !== undefined && (
              <g className="marker marker--cg" transform={`translate(${num(cgPx)} ${num(cy)})`}>
                <circle r="7" />
                <path d="M0 0 L7 0 A7 7 0 0 1 0 7 Z" />
                <path d="M0 0 L-7 0 A7 7 0 0 1 0 -7 Z" />
              </g>
            )}
            {!back && cpPx !== undefined && (
              <g className="marker marker--cp" transform={`translate(${num(cpPx)} ${num(cy)})`}>
                <circle r="6" />
              </g>
            )}
          </svg>
          <figcaption className="sr-only">{description}</figcaption>
        </figure>
      </div>
    </div>
  );
}
